import axios from 'axios';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

export interface SendDocumentRequest {
  to: string;
  cc?: string[];
  subject: string;
  body: string;
  documentType: string;
  documentId: string;
  payLink?: string;
  // item 553 — an optional CTA rendered as a button in the branded email (e.g. the proposal
  // "Approve proposal" link) instead of pasting a long raw URL into the message body.
  actionLabel?: string;
  actionUrl?: string;
  // item 799 — suppress the document PDF attachment (client order-placed confirmation)
  noAttachment?: boolean;
}

// item 111 — "Send" a document to the client (stub email provider until go-live)
export const sendDocument = async (req: SendDocumentRequest): Promise<void> => {
  await axios.post(`${apiUrl()}email/send-document`, req);
};

// item 32 — fetch a (stub) Stripe pay link for an invoice
// item 298 — ACH debit is always offered; card only when allowCard is checked for
// THIS send (per-send opt-in, never sticky)
export const getInvoicePayLink = async (invoiceId: string, allowCard = false): Promise<string> => {
  const { data } = await axios.get(`${apiUrl()}payment/paylink/${invoiceId}?allowCard=${allowCard}`);
  return data.url as string;
};

// item 576 — the status-aware subject/body rule lives SERVER-side (DocumentEmailCopy, one
// rule per doc type keyed off the document's status); the send dialog pre-fills from it so
// every entry point previews EXACTLY the copy the backend composes (a finalized invoice says
// it's finalized, an approved proposal says it's approved, a pending proposal keeps the
// review/approve framing). Best-effort — returns null on any failure (dialog keeps its
// generic fallback).
export interface DocumentEmailCopy { subject: string; body: string }
export const getDocumentCopy = async (
  documentType: string,
  documentId: string,
): Promise<DocumentEmailCopy | null> => {
  try {
    const { data } = await axios.get(`${apiUrl()}email/copy`, {
      params: { documentType, documentId },
    });
    if (!data?.subject && !data?.body) return null;
    return { subject: (data.subject as string) ?? '', body: (data.body as string) ?? '' };
  } catch {
    return null;
  }
};

// item 1327 (Chad) — every address the CRM holds for this document's customer, so the Send
// dialog can offer a pick list instead of a blank field to type into. Best-effort: an empty
// list just means the dialog shows no picker, never an error.
export type CrmRecipient = { name: string; label: string; email: string };

export const getDocumentRecipients = async (
  documentType: string,
  documentId: string,
  audience?: 'customer', // item 1474 — a PO's CUSTOMER contact (the order-placed notice), not its vendor
): Promise<CrmRecipient[]> => {
  try {
    const { data } = await axios.get(`${apiUrl()}email/recipients`, {
      params: { documentType, documentId, ...(audience ? { audience } : {}) },
    });
    return (data?.recipients as CrmRecipient[]) ?? [];
  } catch {
    return [];
  }
};

// item 318 — resolve the routed recipient (Invoice→customer accounting email,
// Proposal→assigned staff, PO→vendor orders email; falls back to the contact's main email)
// so the Send dialog can pre-fill "To". Best-effort — returns '' on any failure.
export const getDocumentRecipient = async (
  documentType: string,
  documentId: string,
): Promise<string> => {
  try {
    const { data } = await axios.get(`${apiUrl()}email/recipient`, {
      params: { documentType, documentId },
    });
    return (data?.to as string) ?? '';
  } catch {
    return '';
  }
};
